import {Accordion, ShowMore} from '@udemy-v2/react-reveal-components';
import classNames from 'classnames';
import {observer} from 'mobx-react';
import React from 'react';

import {safelySetInnerHTML} from '@udemy-v2/utils';
import {useI18n} from '@udemy-v2/i18n';
import styles from './course-description.module.scss';

interface TargetAudienceProps {
    items: string[];
}

export const TargetAudience: React.FC<TargetAudienceProps> = ({items}) => {
    const {gettext} = useI18n();

    if (items.length === 0) {
        return null;
    }
    const audienceItems = items.map((audience, index) => <li key={index}>{audience}</li>);
    return (
        <div className={styles.audience} data-purpose="target-audience">
            <h2 className={classNames('ud-heading-xl', styles['audience-title'])}>
                {gettext('Who this course is for:')}
            </h2>
            <ul className={styles['audience-list']}>{audienceItems}</ul>
        </div>
    );
};

interface DescriptionProps {
    description?: string;
    targetAudience: string[];
    isCollapsible?: boolean;
    className?: string;
    titleTypography?: 'ud-heading-lg' | 'ud-heading-xl';
    onAccordionToggle?: () => void;
}

export const Description: React.FC<DescriptionProps> = observer(
    ({
        description,
        targetAudience,
        isCollapsible = false,
        className = 'component-margin',
        titleTypography = 'ud-heading-xl',
        onAccordionToggle,
    }) => {
        const {gettext} = useI18n();

        if (!description) {
            return null;
        }

        const getAccordionSize = () => {
            switch (titleTypography) {
                case 'ud-heading-lg':
                    return 'large';
                case 'ud-heading-xl':
                default:
                    return 'xlarge';
            }
        };

        const title = gettext('Description');

        if (!isCollapsible) {
            return (
                <div
                    className={classNames(`ud-text-sm ${className}`, styles.description)}
                    data-purpose="course-description"
                >
                    <h2
                        data-purpose="description-title"
                        className={classNames(titleTypography, styles['description-header'])}
                    >
                        {title}
                    </h2>
                    <ShowMore
                        ariaLabelExpanded={gettext('Show less description')}
                        ariaLabelCollapsed={gettext('Show more description')}
                        collapsedHeight={221}
                        withGradient={true}
                    >
                        <div
                            {...safelySetInnerHTML({
                                descriptionOfCaller: 'description:description',
                                html: description,
                            })}
                        />
                        <TargetAudience items={targetAudience} />
                    </ShowMore>
                </div>
            );
        }

        return (
            <Accordion size={getAccordionSize()} className={className}>
                <Accordion.Panel title={title} onToggle={onAccordionToggle}>
                    <div
                        className={classNames('ud-text-sm', styles.description)}
                        data-purpose="course-description"
                    >
                        <div
                            {...safelySetInnerHTML({
                                descriptionOfCaller: 'description:description',
                                html: description,
                            })}
                        />
                        <TargetAudience items={targetAudience} />
                    </div>
                </Accordion.Panel>
            </Accordion>
        );
    },
);
